import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { NavigationEnd, NavigationError, Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import forOwn from 'lodash/forOwn';
import trim from 'lodash/trim';
import { PageMetaService } from '@shared/shared-meta/page-meta.service';
import moment from 'moment';
import { LocaleSpecification } from 'moment';
import { TranslatesService } from './translates';
import {
  CieUtilsModalManagerService,
  CUSTOM_LOCALE_SPECS,
  IGiftCardIntegrationState,
  SentryLoggerService
} from '@cakeiteasy/web-utils';
import { ContextProviderService } from '@shared/services/context-provider.service';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { LayoutContextService } from '@shared/services/layout-context.service';
import { GiftCardsManagerService } from '@cakeiteasy/order-process';
import { filter, switchMap, take } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  public isBrowser: boolean;

  // required to track route change events
  constructor(@Inject('AppConfig') public config: any,
              @Inject(PLATFORM_ID) private _platformId: any,
              private _router: Router,
              private _context: ContextProviderService,
              private _layoutContext: LayoutContextService,
              private _toaster: ToastrService,
              private _translateService: TranslatesService,
              private _translator: TranslateService,
              private _modalManager: CieUtilsModalManagerService,
              private _sentryLogger: SentryLoggerService,
              private _giftCardsManager: GiftCardsManagerService,
              public meta: PageMetaService) {

    this.isBrowser = isPlatformBrowser(this._platformId);

    if (this.isBrowser) {
      let prevUrl = '';
      this._router.events.subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.onNavigationEnd(event, prevUrl);
          prevUrl = event.url;
        }

        if (event instanceof NavigationError) {
          this.onNavigationError(event);
        }
      });
    }

    this.meta.updateCanonicalUrl();
    this._setMomentLocale();
  }

  public onNavigationError(event: NavigationError) {
    if (isPlatformBrowser(this._platformId)) {
      if (!navigator.onLine) {
        this._toaster.error('Oops...', this._translator
          .instant('common.unexpectedErrorReloadPage'));
      } else {
        if (event.error.type === 'missing' || /Loading chunk {\d}+ failed/i.test(event.error.message)) {
          this._sentryLogger.addBreadcrumbs({
            category: 'Navigation Error',
            level: 'error',
            message: 'Chunk not found during navigation in Browser, force page reloading'
          });
          this._sentryLogger.captureException(event.error);
          window.location.reload();
        } else {
          this._sentryLogger.addBreadcrumbs({
            category: 'Navigation Error',
            level: 'error',
            message: 'Unexpected error during navigation in Browser'
          });
          this._sentryLogger.captureException(event.error);
        }
      }
    }
  }

  public onNavigationEnd(event: NavigationEnd, prevUrl = '') {
    // set to null to reset it from prev page
    this.meta.setPageJsonLd(null);
    this.meta.updateCanonicalUrl();

    // if page is changed scroll to top and close modals
    if (prevUrl && !this._isEqualPath(trim(event.url.split('#')[0], '/'), trim(prevUrl.split('#')[0], '/'))) {
      setTimeout(() => {
        if (this._layoutContext.scrollOnTopOnPageChange) {
          window.scroll(0, 0);
        }
        this._modalManager.closeAllModals();
      }, 10);
    } else {
      // if page is not changed and no hash -> close all
      if (event.url.indexOf('#') == -1 && prevUrl.indexOf('#') >= 0) {
        this._modalManager.closeAllModals();
        // otherwise close latest open if user goes back
      } else if (event.url.indexOf('#') >= 0 && prevUrl.indexOf('#') >= 0) {
        this._modalManager.closeLatestIfHashEqual(prevUrl.split('#')[1]);
      }
    }

  }

  ngOnInit(): void {
    if (this.isBrowser) {
      this._getIntegrationState();
    }
  }

  private _setMomentLocale() {
    const LOCAL_MOMENT_CODES_MAP = {
      'no': 'nb',
      'en': 'en-gb',
    };

    const langCode = this._translateService.getCurrentLang();
    let locale = LOCAL_MOMENT_CODES_MAP[langCode] || langCode;
    moment.locale(locale);

    moment.updateLocale(locale, {
      week: { dow: 0, doy: 6 }
    });

    forOwn(CUSTOM_LOCALE_SPECS, (options: LocaleSpecification, localeKey) => {
      if (locale === localeKey) {
        moment.updateLocale(localeKey, options);
      }
    });
  }

  private _isEqualPath(pathA: string = '', pathB: string = '') {
    return this._getUrlWithoutQuery(pathA) === this._getUrlWithoutQuery(pathB);
  }

  private _getUrlWithoutQuery(url: string = '') {
    let questionMarkIndex = url.indexOf('?');
    if (questionMarkIndex === -1) {
      return url;
    }

    return url.substr(0, questionMarkIndex);
  }

  private _getIntegrationState(): Promise<IGiftCardIntegrationState> {
    return new Promise<IGiftCardIntegrationState>(resolve => {
      this._context.countryCode$
        .pipe(filter(code => Boolean(code)), take(1))
        .pipe(switchMap((code) => this._giftCardsManager.getIntegrationState(code)))
        .subscribe((state: IGiftCardIntegrationState) => {
          resolve(state);
        });
    });
  }
}
